import { ref, onMounted, onUnmounted } from 'vue';

export interface PullRefreshOptions {
  threshold?: number; // 触发刷新的阈值（像素）
  onRefresh: () => Promise<void> | void;
}

export function usePullRefresh(options: PullRefreshOptions) {
  const { threshold = 80, onRefresh } = options;

  const isRefreshing = ref(false);
  const pullDistance = ref(0);
  const isPulling = ref(false);

  let startY = 0;
  let currentY = 0;
  let isDragging = false;

  // 触摸开始
  function onTouchStart(e: TouchEvent) {
    // 只在页面顶部时才响应
    if (window.scrollY > 0) return;

    startY = e.touches[0].clientY;
    isDragging = true;
  }

  // 触摸移动
  function onTouchMove(e: TouchEvent) {
    if (!isDragging || isRefreshing.value) return;

    currentY = e.touches[0].clientY;
    const distance = currentY - startY;

    // 只在向下拉动时响应
    if (distance > 0) {
      // 添加阻尼效果
      pullDistance.value = distance * 0.4;
      isPulling.value = pullDistance.value >= threshold;

      // 阻止默认滚动行为
      if (pullDistance.value > 0) {
        e.preventDefault();
      }
    }
  }

  // 触摸结束
  async function onTouchEnd(e: TouchEvent) {
    if (!isDragging) return;

    isDragging = false;

    // 如果拉动距离超过阈值，触发刷新
    if (isPulling.value && !isRefreshing.value) {
      isRefreshing.value = true;
      pullDistance.value = threshold;

      try {
        await onRefresh();
      } finally {
        // 刷新完成后隐藏指示器
        setTimeout(() => {
          isRefreshing.value = false;
          pullDistance.value = 0;
          isPulling.value = false;
        }, 300);
      }
    } else {
      // 未达到阈值，重置
      pullDistance.value = 0;
      isPulling.value = false;
    }
  }

  // 挂载事件监听
  onMounted(() => {
    // 检测是否支持触摸事件
    if ('ontouchstart' in window) {
      document.addEventListener('touchstart', onTouchStart, { passive: false });
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);
    }
  });

  // 清理事件监听
  onUnmounted(() => {
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', onTouchEnd);
  });

  return {
    isRefreshing,
    pullDistance,
    isPulling,
  };
}
